import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Composer, Container, Header, MessageList, useWebchat } from "@botpress/webchat";
import { Edit } from "@mui/icons-material";
import { Box, Divider, Stack, Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import constants from "../../assets/constants.js";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import * as BotpressSessionActions from "../../state/redux/botpressSession";
import AssessmentSelectionDialog from "../dialogs/assessmentSelectionDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import Checkbox from "../ui/omniCheckbox";
import OmniMenuItem from "../ui/omniMenuItem.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import OmniTextField from "../ui/omniTextField.js";
import Widget from "../ui/widget.js";
import WidgetContent from "../ui/widgetContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressTestHarness = ({
  botpressBotODM,
  userODM,
  widgetSize = "large",
  minHeight = "333px",
  loadConversationRequest,
  deleteChatRequest,
  conversation,
  conversationFeedback,
  deleteFeedbackRequest,
  updateFeedbackRequest,
  updateAssessmentRequest,
  configurations,
  rerunConversationId,
  rerunConversationNumber,
  setRerunConversationIdRequest,
  rerunFeedbackNumber,
}) => {
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const webchatClientID = botpressBotODM?.omniData.webchatClientId;

  const calculateUserData = (theData, userODM) => {
    try {
      //try is excessive but saftey first
      if (userODM && theData) {
        theData.firstName = userODM.omniData.givenName;
        theData.lastName = userODM.omniData.familyName;
      }
      theData.origin = constants.botpressConversationOriginEnums.harness;
    } catch (error) {}
    return theData;
  };

  const [userData, setUserData] = useState(
    calculateUserData(botpressBotODM?.omniData.userData, userODM),
  );

  const [editUserData, setEditUserData] = useState(null);

  const [botData, setBotData] = useState(botpressBotODM?.omniData.botData);
  const [editBotData, setEditBotData] = useState(null);

  const [languageCode, setLanguageCode] = useState(botpressBotODM?.omniData.userData.languageCode);

  const botDisplayName = botpressBotODM?.omniData.botData?.botName;

  const [cancelCurrentAssessment, setCancelCurrentAssessment] = useState(false);

  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const [rerunComment, setRerunComment] = useState(null);

  const [newConversationOnLaunch, setNewConversationOnLaunch] = useState(true);

  const { client, on, messages, isTyping, user, clientState, newConversation } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here
    feedbackEnabled: true,
    storageKey: `botpress-testHarness-${webchatClientID}`,
    storageLocation: "localStorage",
  });

  const enrichedMessages = useMemo(
    () =>
      messages.map(message => {
        const { authorId } = message;
        const direction = authorId === user?.userId ? "outgoing" : "incoming";
        return {
          ...message,
          direction,
          sender:
            direction === "outgoing"
              ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
              : { name: botData?.botName ?? "Bot", avatar: botData?.botAvatar },
        };
      }),
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [botData?.botName, messages, user?.userId, user?.name, user?.pictureUrl],
  );

  const endConversation = (wasCancelled = false) => {
    try {
      const omniPayload = {
        omniEvent: "endConversation",
        omniData: {},
      };
      client.sendEvent(omniPayload).then(() => {
        setIsWebchatOpen(false);
        if (wasCancelled) {
          enqueueSnackbar("Conversation has been cancelled. No Assessment information was saved.", {
            variant: "warning",
          });
        } else {
          enqueueSnackbar("Any Feedback you left will be available in a few minutes", {
            variant: "success",
          });
        }
      });
    } catch (error) {
      console.error("Error ending conversation:", error);
    }
  };

  const cleanupAssessmentAfterCancel = () => {
    setLastMessage(null);
    setMessageArray(null);
    setQuestionIndex(-1);
    setAssessmentInProgress(false);

    setPostAssessmentDisplay(false);
    setSelectedAssessmentTemplate(null);
    setRerunComment(null);
    setCancelCurrentAssessment(false);
    endConversation(true);
  };

  const endAssessment = (conversationId, messageCount, elapsedSeconds) => {
    try {
      const omniPayload = {
        omniEvent: "endConversation",
        omniData: {},
      };
      client.sendEvent(omniPayload).then(() => {
        setIsWebchatOpen(false);
      });

      enqueueSnackbar("Finalizing the conversation for you to give feedback...", {
        variant: "success",
      });

      let commentText = "";

      let configuration = "";
      if (configurations && configurations.length > 0) {
        let aiConfigs = configurations.filter(
          config => config.configType === constants.botpressConfigTypeEnums.aiModel,
        );
        for (let index = 0; index < aiConfigs.length; ++index) {
          let theConfig = aiConfigs[index];
          configuration += `${theConfig.label}: ${theConfig.value}|`;
        }
      }

      if (configuration.length === 0) {
        configuration = "unknown";
      }

      let assessmentTypeText = selectedAssessmentTemplate
        ? selectedAssessmentTemplate.omniData.omniType
        : "Custom";
      if (rerunComment) {
        assessmentTypeText = "Rerun";
      }

      let assessmentNameText = selectedAssessmentTemplate
        ? selectedAssessmentTemplate.omniData.title
        : "Custom Assessment";
      if (rerunComment) {
        assessmentTypeText = `Conversation #${postRunConversationNumber}`;
        commentText = rerunComment;
      }

      //store conversation id to load feedback dialog
      //create new feedback

      const theRecord = {
        id: null,
        comment: commentText,
        questionCount: messageCount,
        archived: false,
        userName: userODM
          ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}`
          : "Unknown User",
        conversationId: conversationId,
        elapsedSeconds: elapsedSeconds,
        assessmentType: assessmentTypeText,
        assessmentName: assessmentNameText,
        configuration: configuration,
      };
      // "Run conversation" clean up
      setMessageArray(null);
      setQuestionIndex(-1);
      setAssessmentInProgress(false);

      updateAssessmentRequest({ assessmentRecord: theRecord });

      setPostAssessmentDisplay(true);
      loadConversationRequest(conversationId);
      selectedAssessmentTemplate(null);
      setRerunComment(null);
      setCancelCurrentAssessment(false);
      setPostRunConversationNumber(null);
    } catch (error) {
      console.error("Error ending assessment:", error);
    }
  };

  const startWebchat = () => {
    if (newConversationOnLaunch) {
      newConversation().then(() => {
        client.updateUser({
          data: userData,
        });
        setIsWebchatOpen(true);
      });
    } else {
      client.updateUser({
        data: userData,
      });
      setIsWebchatOpen(true);
    }
  };

  const handleRerun = (conversationID, rerunConversationNumber) => {
    try {
      //load conversation to get messages
      // get the inputs
      // Start the conversation with file contents

      BotpressAPI.loadConversation({
        botODM: botpressBotODM,
        conversationId: conversationID,
      }).then(returnedMessages => {
        let lines = returnedMessages
          .reverse()
          .filter(message => message.direction === "incoming")
          .map(message => message.payload.text || "");

        loadConversationRequest(null);
        setLastMessage(null);
        setAssessmentInProgress(true);
        setPostRunConversationNumber(rerunConversationNumber);
        setMessageArray(lines);
        startWebchat();
        setCountdown(10);
        setRerunConversationIdRequest(null);
      });
    } catch (error) {
      console.error("Error rerunning conversation:", error);
    }
  };

  useEffect(() => {
    if (rerunComment) {
      try {
        handleRerun(rerunConversationId, rerunConversationNumber);
      } catch (error) {
        console.error(error);
      }
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunComment]);

  const handleFileSelect = event => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.includes("text") && !file.name.endsWith(".txt")) {
      enqueueSnackbar("Please select a text file (.txt)", {
        variant: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = readerEvent => {
      try {
        const fileContent = readerEvent.target?.result;
        if (!fileContent || typeof fileContent !== "string") {
          enqueueSnackbar("Unable to read file contents", {
            variant: "error",
          });
          return;
        }

        // Split file into lines, filter empty lines
        const lines = fileContent
          .split("\n")
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (lines.length === 0) {
          enqueueSnackbar("File is empty. Please select a file with content.", {
            variant: "error",
          });
          return;
        }

        // Start the conversation with file contents
        loadConversationRequest(null);
        setLastMessage(null);
        setAssessmentInProgress(true);
        setMessageArray(lines);
        startWebchat();
        setCountdown(10);
      } catch (error) {
        console.error("Error reading file:", error);
        enqueueSnackbar("Error reading file. Please try again.", {
          variant: "error",
        });
      }
    };
    reader.readAsText(file);
  };

  // const [userInitialized, SetUserInitialized] = useState(false);

  // useEffect(() => {
  //   if (client && userData && !userInitialized) {
  //     try {
  //       debugger;
  //       client.updateUser({
  //         data: userData,
  //       });
  //       SetUserInitialized(true);
  //     } catch (error) {
  //       console.error(error);
  //     }
  //   }
  // }, [client, userData, userInitialized]);

  //#region: Auto Ask Questions

  const [postAssessmentDisplay, setPostAssessmentDisplay] = useState(false);
  const [selectAssessment, setSelectAssessment] = useState(false);
  const [assessmentInProgress, setAssessmentInProgress] = useState(false);
  const [messageArray, setMessageArray] = useState(null);
  const [postRunConversationNumber, setPostRunConversationNumber] = useState(null);
  const [countdown, setCountdown] = useState(-1);
  const [startTime, setStartTime] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(-1);
  const [fileInputRef, setFileInputRef] = useState(null);

  const [selectedAssessmentTemplate, setSelectedAssessmentTemplate] = useState(null);

  useEffect(() => {
    if (messageArray && countdown < 0 && questionIndex >= 0) {
      if (cancelCurrentAssessment) {
        cleanupAssessmentAfterCancel();
      } else if (questionIndex >= messageArray.length) {
        let conversationId = null;
        if (messages.length > 0) {
          const lastMessage = messages[0];
          conversationId = lastMessage.conversationId;
        }
        let messageCount = 0;
        messageCount = messageArray.length;

        setLastMessage(null);
        setMessageArray(null);
        setQuestionIndex(-1);
        setAssessmentInProgress(false);

        const endTime = Date.now();
        const elapsedSeconds = Math.floor((endTime - startTime) / 1000);

        endAssessment(conversationId, messageCount, elapsedSeconds);
      } else {
        let theText = messageArray[questionIndex];
        client?.sendMessage({
          type: "text",
          text: theText,
        });
      }
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIndex, messageArray, countdown, cancelCurrentAssessment]);

  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    const unsubscribe = on("message", message => {
      if (message.authorId !== user?.userId && countdown < 0) {
        try {
          setLastMessage(message);
        } catch (error) {
          console.error("Error in TTS message handling:", error);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [on, user, countdown]);

  useEffect(() => {
    //ok start the automated process
    if (lastMessage && questionIndex >= 0) {
      const delayGotoNextQuestion = setTimeout(() => {
        //todo this just got harder.

        setQuestionIndex(prev => prev + 1);
      }, 1000);
      return () => clearTimeout(delayGotoNextQuestion);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  useEffect(() => {
    //ok start the automated process
    if (countdown > 0 && cancelCurrentAssessment) {
      setCancelCurrentAssessment(false);
      setCountdown(-1);
      cleanupAssessmentAfterCancel();
    } else if (countdown > 0) {
      const countdownFunction = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(countdownFunction);
    } else if (countdown === 0) {
      setStartTime(Date.now());
      setQuestionIndex(0);
      setCountdown(-1);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, cancelCurrentAssessment]);
  //#endregion

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      {!isWebchatOpen && (
        <WidgetTitle title={"Launch Pad"} text={botDisplayName}>
          <OmniResponsiveButton
            tooltipTitle="Edit the User Settings"
            buttonText="User"
            sx={{ marginRight: theme.spacing(2) }}
            onClick={() => {
              setEditUserData(JSON.stringify(userData, null, 2));
            }}
            icon={<Edit />}
          />
          <OmniResponsiveButton
            tooltipTitle="Edit the AI Settings"
            buttonText="Bot"
            onClick={() => {
              setEditBotData(JSON.stringify(botData, null, 2));
            }}
            icon={<Edit />}
          />
        </WidgetTitle>
      )}

      {!isWebchatOpen && (
        <WidgetContent noMargins={true}>
          <Grid container size={{ xs: 12 }}>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  spacing: "8px",
                  marginRight: theme.spacing(1),
                  marginLeft: theme.spacing(1),
                  gap: theme.spacing(2),
                  padding: theme.spacing(1),
                }}
              >
                <OmniTextField
                  label="Initial Language"
                  select
                  variant="outlined"
                  id={"language"}
                  value={languageCode}
                  onChange={event => {
                    setLanguageCode(event.target.value);
                    let newUserData = JSON.parse(userData);
                    newUserData.languageCode = event.target.value;
                    setUserData(JSON.stringify(newUserData, null, 2));
                  }}
                  placeholder={"language"}
                  required
                >
                  <OmniMenuItem key={"en-US"} value={"en-US"}>
                    {"English (US)"}
                  </OmniMenuItem>
                  <OmniMenuItem key={"es-US"} value={"es-US"}>
                    {"Spanish (US)"}
                  </OmniMenuItem>
                  <OmniMenuItem key={"ru-RU"} value={"ru-RU"}>
                    {"Russian (RU)"}
                  </OmniMenuItem>
                  <OmniMenuItem key={"sw-KE"} value={"sw-KE"}>
                    {"Swahili (KE)"}
                  </OmniMenuItem>
                </OmniTextField>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  marginLeft: theme.spacing(2),
                  marginTop: theme.spacing(1),

                  gap: theme.spacing(2),
                  padding: theme.spacing(1),

                  // alignSelf: "flex-end",
                  // justifyContent: "flex-end",
                  // textAlign: "right",
                }}
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="secondary"
                        checked={newConversationOnLaunch}
                        onChange={() => setNewConversationOnLaunch(!newConversationOnLaunch)}
                        name="newConversationOnLaunch"
                      />
                    }
                    label="Restart Conversation on Launch"
                  />
                </FormGroup>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <Divider sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2) }} />
            </Grid>
            <Grid item padding={2} size={{ xs: 12, sm: 6 }}>
              <Button
                sx={{
                  height: "auto",
                  borderRadius: theme.spacing(1),
                  width: "stretch",
                }}
                tooltipTitle="Start Conversation"
                variant="contained"
                color="secondary"
                onClick={() => {
                  startWebchat();
                }}
              >
                <Stack>
                  <Typography
                    variant="h5"
                    sx={{
                      borderBottom: `2px solid `,
                      margin: theme.spacing(1),
                      paddingBottom: theme.spacing(1),
                      marginBottom: 0,
                    }}
                  >
                    New Conversation
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      margin: theme.spacing(1),
                    }}
                  >
                    Click Here
                  </Typography>
                </Stack>
              </Button>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="body1"
                sx={{
                  margin: 0,
                  marginBottom: 0,
                  paddingRight: theme.spacing(2),
                  paddingTop: theme.spacing(2),
                }}
              >
                Start a test conversation with the bot to try it out. You can give real-time
                feedback directly through the UI, and your comments will appear below.
              </Typography>
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <Divider sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2) }} />
            </Grid>
            <Grid item padding={2} size={{ xs: 12, sm: 6 }}>
              <Button
                sx={{
                  height: "auto",
                  borderRadius: theme.spacing(1),
                  width: "stretch",
                }}
                tooltipTitle="Select Conversation"
                variant="contained"
                color="secondary"
                onClick={() => {
                  setSelectAssessment(true);
                }}
              >
                <Stack>
                  <Typography
                    variant="h5"
                    sx={{
                      borderBottom: `2px solid `,
                      margin: theme.spacing(1),
                      paddingBottom: theme.spacing(1),
                      marginBottom: 0,
                    }}
                  >
                    Test Conversation
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      margin: theme.spacing(1),
                    }}
                  >
                    Click Here
                  </Typography>
                </Stack>
              </Button>
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="body1"
                sx={{
                  margin: 0,
                  marginBottom: 0,
                  paddingRight: theme.spacing(2),
                  paddingTop: theme.spacing(2),
                }}
              >
                Select from a list of pre-configured conversations designed to test specific
                scenarios, knowledge bases, or features of the bot. This is a great way to quickly
                run through common use cases.
              </Typography>
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <Divider sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2) }} />
            </Grid>
            <Grid item padding={2} size={{ xs: 12, sm: 6 }}>
              <Button
                sx={{
                  height: "auto",
                  borderRadius: theme.spacing(1),
                  width: "stretch",
                }}
                tooltipTitle="Run a conversation from a text file"
                variant="contained"
                color="secondary"
                onClick={() => {
                  fileInputRef?.click();
                }}
              >
                <Stack>
                  <Typography
                    variant="h5"
                    sx={{
                      borderBottom: `2px solid `,
                      margin: theme.spacing(1),
                      paddingBottom: theme.spacing(1),
                      marginBottom: 0,
                    }}
                  >
                    Upload Conversation
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      margin: theme.spacing(1),
                    }}
                  >
                    Click Here
                  </Typography>
                </Stack>
              </Button>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="body1"
                sx={{
                  margin: 0,
                  marginBottom: 0,
                  paddingRight: theme.spacing(2),
                  paddingTop: theme.spacing(2),
                }}
              >
                Rerun a saved conversation (JSON file) or upload a list of your own questions (text
                file) to see how the bot responds.
              </Typography>
            </Grid>
          </Grid>
        </WidgetContent>
      )}
      {isWebchatOpen && (
        <Container
          connected={clientState !== "disconnected"}
          style={{
            minHeight: "100%",
            display: isWebchatOpen ? "flex" : "none",
            marginBottom: "-8px",
          }}
        >
          <Header
            // onOpenChange={() => console.log('Override the header open change')}
            defaultOpen={false}
            closeWindow={() => endConversation()}
            // closeWindow={() => setIsWebchatOpen(false)}
            // restartConversation={newConversation}
            configuration={botData}
          />
          <MessageList
            botAvatar={botData?.botAvatar}
            botName={botData?.botName}
            botDescription={botData?.botDescription}
            isTyping={isTyping}
            headerMessage="Chat History"
            showMarquee={true}
            messages={enrichedMessages}
            sendMessage={client?.sendMessage}
            addMessageFeedback={client?.addMessageFeedback}
          />
          <Composer
            disableComposer={false}
            isReadOnly={false}
            allowFileUpload={true}
            connected={clientState !== "disconnected"}
            sendMessage={client?.sendMessage}
            uploadFile={client?.uploadFile}
            composerPlaceholder="Type a message..."
          />
        </Container>
      )}

      {assessmentInProgress && (
        <Box
          style={{
            position: "absolute",
            zIndex: 9999,
            minHeight: "100%",
            display: "flex",
            color: "black",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            width: "100%",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box
            style={{
              display: "flex",
              padding: theme.spacing(2),
              borderRadius: theme.spacing(2),
              margin: theme.spacing(2),
              width: "90%",
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              boxShadow: theme.shadows[3],
            }}
          >
            <Typography
              variant="h5"
              sx={{
                marginBottom: theme.spacing(2),
              }}
            >
              {countdown > 0
                ? `Starting Assessment: ${selectedAssessmentTemplate?.omniData.title || rerunComment ? `Rerun Conversation` : "Custom Conversation"}`
                : `Assessment: ${selectedAssessmentTemplate?.omniData.title || rerunComment ? `Rerun Conversation` : "Custom Conversation"}`}
            </Typography>
            {countdown > 0 && (
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  magin: theme.spacing(2),

                  background: theme.palette.secondary.main,
                  color: theme.palette.secondary.contrastText,
                  border: `4px solid ${theme.palette.secondary.contrastText}`,
                }}
              >
                <Typography variant="h3">{countdown}</Typography>
              </Avatar>
            )}
            {countdown < 0 && messageArray && (
              <Typography
                variant="h6"
                sx={{
                  marginBottom: theme.spacing(2),
                }}
              >
                {`Asking Question ${questionIndex + 1} of ${messageArray.length}`}
              </Typography>
            )}
            {countdown < 0 && messageArray && (
              <LinearProgress
                variant="determinate"
                color="secondary"
                sx={{
                  width: "80%",
                  height: theme.spacing(2),
                  borderRadius: theme.spacing(1),
                  boxShadow: theme.shadows[3],
                }}
                value={((questionIndex + 1) / messageArray.length) * 100}
              />
            )}
          </Box>
          <Button
            variant="contained"
            color="secondary"
            sx={{ position: "absolute", bottom: theme.spacing(2), right: theme.spacing(2) }}
            onClick={() => {
              setCancelCurrentAssessment(true);
            }}
          >
            Cancel Assessment
          </Button>
        </Box>
      )}

      {rerunConversationId && (
        <TextInputDialog
          inputLabel={`Comment for Assessment`}
          open={rerunConversationId}
          dialogTitle={"Rerun Conversation"}
          dialogMessage={`The information from the new conversation will saved as an assessment.`}
          inputPlaceholder={"Why are you rerunning this conversation?"}
          defaultValue={`${rerunConversationNumber ? `Rerun of Conversation: #${rerunConversationNumber}\r\n` : ""}${rerunFeedbackNumber ? `Testing User Feedback: #${rerunFeedbackNumber}` : ""}`}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          onAccept={theValue => {
            setRerunComment(theValue);
          }}
          onClose={() => {
            setRerunConversationIdRequest(null);
          }}
        />
      )}

      {editUserData && (
        <TextInputDialog
          inputLabel={`User Data JSON`}
          open={editUserData}
          dialogTitle={"Update User Data"}
          dialogMessage={`Paste in some well formed JSON.`}
          defaultValue={editUserData}
          inputPlaceholder={"User Data JSON"}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          maxWidth={"sm"}
          rows={10}
          onAccept={theValue => {
            try {
              //validate
              setUserData(theValue);
              setEditUserData(null);
            } catch (error) {
              setEditUserData(null);
              console.log(error);
            }
          }}
          onClose={() => {
            setEditUserData(null);
          }}
        />
      )}

      {editBotData && (
        <TextInputDialog
          inputLabel={`Bot Configuration JSON`}
          open={editBotData}
          dialogTitle={"Update Bot Configuration"}
          dialogMessage={`Paste in some well formed JSON.`}
          defaultValue={editBotData}
          inputPlaceholder={"Bot Configuration JSON"}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          maxWidth={"sm"}
          rows={15}
          onAccept={theValue => {
            try {
              //validate
              setBotData(theValue);
              setEditBotData(null);
            } catch (error) {
              setEditBotData(null);
              console.log(error);
            }
          }}
          onClose={() => {
            setEditBotData(null);
          }}
        />
      )}

      {selectAssessment && (
        <AssessmentSelectionDialog
          open={selectAssessment}
          onAccept={assessment => {
            const theQuestions = [];
            for (let i = 0; i < assessment.omniData.questions.length; ++i) {
              theQuestions.push(assessment.omniData.questions[i].text);
            }
            setSelectedAssessmentTemplate(assessment);
            loadConversationRequest(null);
            setLastMessage(null);
            setAssessmentInProgress(true);
            setMessageArray(theQuestions);
            startWebchat();
            setCountdown(10);
            setSelectAssessment(false);
          }}
          onClose={() => {
            setSelectAssessment(false);
          }}
        />
      )}

      <input
        ref={el => setFileInputRef(el)}
        type="file"
        accept=".txt,text/plain"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {conversation != null && postAssessmentDisplay && (
        <BotpressConversationDialog
          conversationId={conversation.conversationId}
          conversation={conversation}
          dialogTitle={`Conversation Transcription`}
          conversationFeedback={conversationFeedback}
          deleteFeedbackRequest={deleteFeedbackRequest}
          updateFeedbackRequest={updateFeedbackRequest}
          userODM={userODM}
          open={conversation != null}
          defaultValue={conversation}
          onAccept={() => {
            loadConversationRequest(null);
            setPostAssessmentDisplay(false);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setPostAssessmentDisplay(false);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: conversation.id });
            loadConversationRequest(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
            setPostAssessmentDisplay(false);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  botpressBotODM: state.botpressSession.botpressBotODM,
  userODM: state.userSession.userODM,

  conversation: state.botpressSession.conversation,
  conversationFeedback: state.botpressSession.conversationFeedback,

  configurations: state.botpressSession.configurations,

  rerunConversationId: state.botpressSession.rerunConversationId,
  rerunConversationNumber: state.botpressSession.rerunConversationNumber,
  rerunFeedbackNumber: state.botpressSession.rerunFeedbackNumber,
});

const mapDispatchToProps = {
  deleteChatRequest: BotpressSessionActions.deleteChatRequest,
  loadConversationRequest: BotpressSessionActions.loadConversationRequest,
  deleteFeedbackRequest: BotpressSessionActions.deleteFeedbackRequest,
  updateFeedbackRequest: BotpressSessionActions.updateFeedbackRequest,
  updateAssessmentRequest: BotpressSessionActions.updateAssessmentRequest,
  setRerunConversationIdRequest: BotpressSessionActions.setRerunConversationIdRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BotpressTestHarness));
